import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Plus, Settings } from 'lucide-react';
import { useSpace } from '../context/SpaceContext';
import { Space } from '../services/api';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

interface DashboardSpaceSwitchProps {
  className?: string;
  /** When provided (e.g. on /spaces), use instead of Link to /spaces for Create/Manage. */
  onCreateSpace?: () => void;
  onManageSpaces?: () => void;
  /** 'dashboard' = outline/card (default); 'workspace' = sanctuary purple styling. */
  variant?: 'dashboard' | 'workspace';
}

/**
 * Compact space switch for nav or header (dashboard or Memory Workspace).
 * Updates SpaceContext.currentSpace. 0 spaces: hidden; 1/2+: dropdown with Create/Manage (links or callbacks).
 */
export function DashboardSpaceSwitch({
  className,
  onCreateSpace,
  onManageSpaces,
  variant = 'dashboard',
}: DashboardSpaceSwitchProps) {
  const { currentSpace, availableSpaces, setCurrentSpace } = useSpace();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  if (availableSpaces.length === 0) {
    return null;
  }

  const handleSelectSpace = (space: Space) => {
    if (space.space_id !== currentSpace?.space_id) {
      setCurrentSpace(space);
    }
    setOpen(false);
  };

  const triggerLabel = currentSpace?.name ?? availableSpaces[0]?.name ?? t('spaceSwitch.currentSpace', { ns: 'dashboard' });

  const isWorkspace = variant === 'workspace';
  const triggerClass = isWorkspace
    ? 'min-h-[44px] min-w-[44px] gap-1.5 border-workspace-primary-500/30 bg-workspace-surface-100 text-workspace-text-primary hover:bg-workspace-surface-200'
    : 'min-h-[44px] min-w-[44px] gap-1.5 border-border bg-card text-foreground hover:bg-accent/50';
  const dropdownClass = isWorkspace
    ? 'absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-md border border-workspace-primary-500/20 bg-workspace-surface-100 py-1 shadow-lg'
    : 'absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-md border border-border bg-card py-1 shadow-lg';
  const itemClass = isWorkspace
    ? 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-workspace-text-primary hover:bg-workspace-surface-200 focus:bg-workspace-surface-200 focus:outline-none'
    : 'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none';

  return (
    <div ref={ref} className={cn('relative', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(triggerClass, isWorkspace && 'border-workspace-primary-500/30')}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={t('spaceSwitch.ariaLabel', { ns: 'dashboard' })}
        aria-haspopup="listbox"
      >
        {currentSpace && (
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: currentSpace.color }}
            aria-hidden
          />
        )}
        <span className="max-w-[120px] truncate sm:max-w-[160px]">{triggerLabel}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </Button>

      {open && (
        <div role="listbox" className={dropdownClass}>
          {availableSpaces.map((space) => (
            <button
              key={space.space_id}
              type="button"
              role="option"
              aria-selected={space.space_id === currentSpace?.space_id}
              className={itemClass}
              onClick={() => handleSelectSpace(space)}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: space.color }}
                aria-hidden
              />
              <span className="flex-1 truncate">{space.name}</span>
            </button>
          ))}
          <div className={cn('my-1 h-px', isWorkspace ? 'bg-workspace-primary-500/20' : 'bg-border')} />
          {onCreateSpace ? (
            <button
              type="button"
              className={itemClass}
              onClick={() => {
                setOpen(false);
                onCreateSpace();
              }}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              {t('spaceSwitch.createSpace', { ns: 'dashboard' })}
            </button>
          ) : (
            <Link
              to="/spaces"
              className={itemClass}
              onClick={() => setOpen(false)}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              {t('spaceSwitch.createSpace', { ns: 'dashboard' })}
            </Link>
          )}
          {!isWorkspace &&
            (onManageSpaces ? (
              <button
                type="button"
                className={itemClass}
                onClick={() => {
                  setOpen(false);
                  onManageSpaces();
                }}
              >
                <Settings className="h-4 w-4 shrink-0" aria-hidden />
                {t('spaceSwitch.manageSpaces', { ns: 'dashboard' })}
              </button>
            ) : (
              <Link
                to="/spaces"
                className={itemClass}
                onClick={() => setOpen(false)}
              >
                <Settings className="h-4 w-4 shrink-0" aria-hidden />
                {t('spaceSwitch.manageSpaces', { ns: 'dashboard' })}
              </Link>
            ))}
        </div>
      )}
    </div>
  );
}

export default DashboardSpaceSwitch;
